/**
 * Jainsons Lights — Location-Aware Product Sorter
 *
 * Re-scores products on collection pages so items with stock in the customer's
 * city appear first. Delhi is the default city and always shown first.
 *
 * City rules:
 *   - Delhi: always default, always shown
 *   - Mumbai: shown if collection has Mumbai-tagged products (primary non-Delhi city)
 *   - Ahmedabad: shown if collection has Ahmedabad-tagged products
 *   - Bangalore: only shown for chandelier/fan collections with Bangalore stock
 *   - Chennai: only shown for non-decorative collections with Chennai stock (JS-SMS family)
 *   - Jaipur: never shown (serviced from Delhi)
 *
 * Picker shows only cities that have actual products in the current collection.
 * Adding a new city requires tagging products with "Stock in <CityName>" in
 * Shopify Admin — the picker updates automatically from the DOM.
 */

(function () {
  'use strict';

  // ---------- CONFIG ----------

  const KNOWN_STOCK_CITIES = ['delhi', 'mumbai', 'ahmedabad', 'bangalore', 'chennai'];
  // Jaipur intentionally omitted — serviced from Delhi

  const CITY_DISPLAY_NAMES = {
    delhi: 'Delhi',
    mumbai: 'Mumbai',
    ahmedabad: 'Ahmedabad',
    bangalore: 'Bangalore',
    chennai: 'Chennai',
  };

  function getCityDisplayName(city) {
    return CITY_DISPLAY_NAMES[city] || (city.charAt(0).toUpperCase() + city.slice(1));
  }

  // Score weights per customer city. Delhi never drops below 0.7 since it
  // holds the largest regular-stock inventory.
  const CITY_WEIGHTS = {
    delhi: {
      delhi: 1.0,
      mumbai: 0.7,
      ahmedabad: 0.6,
      bangalore: 0.5,
      chennai: 0.5,
    },
    mumbai: {
      mumbai: 1.0,
      delhi: 0.8,
      ahmedabad: 0.7,
      bangalore: 0.5,
      chennai: 0.5,
    },
    ahmedabad: {
      ahmedabad: 1.0,
      mumbai: 0.8,
      delhi: 0.8,
      bangalore: 0.5,
      chennai: 0.5,
    },
    bangalore: {
      bangalore: 1.0,
      mumbai: 0.7,
      delhi: 0.7,
      ahmedabad: 0.6,
      chennai: 0.6,
    },
    chennai: {
      chennai: 1.0,
      bangalore: 0.8,
      mumbai: 0.7,
      delhi: 0.7,
      ahmedabad: 0.5,
    },
    default: {
      delhi: 1.0,
      mumbai: 0.7,
      ahmedabad: 0.6,
      bangalore: 0.5,
      chennai: 0.5,
    },
  };

  // Indian state → nearest stock city
  const STATE_TO_STOCK_CITY = {
    // North — Delhi
    'delhi': 'delhi',
    'haryana': 'delhi',
    'punjab': 'delhi',
    'himachal pradesh': 'delhi',
    'jammu and kashmir': 'delhi',
    'uttarakhand': 'delhi',
    'uttar pradesh': 'delhi',
    'bihar': 'delhi',
    'jharkhand': 'delhi',
    'west bengal': 'delhi',
    'odisha': 'delhi',
    'rajasthan': 'delhi', // Jaipur removed; route to Delhi
    // West — Mumbai or Ahmedabad
    'maharashtra': 'mumbai',
    'goa': 'mumbai',
    'madhya pradesh': 'mumbai',
    'chhattisgarh': 'mumbai',
    'gujarat': 'ahmedabad',
    'dadra and nagar haveli': 'mumbai',
    'daman and diu': 'mumbai',
    // South — Bangalore or Chennai
    'karnataka': 'bangalore',
    'tamil nadu': 'chennai',
    'kerala': 'bangalore',
    'andhra pradesh': 'bangalore',
    'telangana': 'bangalore',
    'puducherry': 'chennai',
    // North East / other — default Delhi
    'assam': 'delhi',
    'arunachal pradesh': 'delhi',
    'manipur': 'delhi',
    'meghalaya': 'delhi',
    'mizoram': 'delhi',
    'nagaland': 'delhi',
    'sikkim': 'delhi',
    'tripura': 'delhi',
  };

  const LOCALSTORAGE_KEY = 'jl_customer_city';
  const LOCALSTORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  const MAX_PRODUCTS_TO_RERANK = 500;
  // Must be large enough to guarantee an exact-city-match product beats a
  // non-matching product at any position. With ~24-48 products per page,
  // baseScore range is ~4–100. A boost of 1000 ensures exact-match products
  // always float above partial-match (0.8×1000=800) or unmatched products.
  const LOCATION_BOOST_POINTS = 1000;

  // Handles containing these patterns are chandelier/fan collections
  const CHANDELIER_FAN_RE = /chandeli|ceiling.fan|jhumar/;
  // Handles containing these patterns are decorative collections
  const DECORATIVE_RE = /decorative|wall.light|wall-light|hanging.light|hanging-light|modern.hanging|modern-hanging|modern.wall|modern-wall|chandeli|jhum/;

  // ---------- UTILITIES ----------

  function normalizeCity(name) {
    if (!name) return null;
    return String(name).toLowerCase().trim();
  }

  function getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^/?]+)/);
    return match ? match[1].toLowerCase() : '';
  }

  function getStockCityForCustomer(cityName, regionName) {
    const city = normalizeCity(cityName);
    if (KNOWN_STOCK_CITIES.includes(city)) return city;
    const region = normalizeCity(regionName);
    if (region && STATE_TO_STOCK_CITY[region]) return STATE_TO_STOCK_CITY[region];
    return 'delhi';
  }

  function getWeight(customerCity, stockCity) {
    const row = CITY_WEIGHTS[customerCity];
    if (row && row[stockCity] !== undefined) return row[stockCity];
    if (customerCity === stockCity) return 1.0;
    if (stockCity === 'delhi') return 0.7;
    return 0.5;
  }

  function loadCachedCity() {
    try {
      const raw = localStorage.getItem(LOCALSTORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.stockCity || !data.savedAt) return null;
      if (Date.now() - data.savedAt > LOCALSTORAGE_TTL_MS) return null;
      return data;
    } catch (_e) {
      return null;
    }
  }

  function saveCity(stockCity, source) {
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify({
        stockCity,
        source,
        savedAt: Date.now(),
      }));
    } catch (_e) { /* ignore */ }
  }

  // ---------- CITY DISCOVERY ----------

  /**
   * Scans the current page's product cards to find which cities actually have
   * stock here, then applies collection-type rules to decide what to show.
   * Returns cities in display order: Delhi first, Mumbai second, then rest.
   */
  function discoverStockCitiesOnPage() {
    const foundCities = new Set();
    document.querySelectorAll('[data-jl-stock-cities]').forEach(el => {
      const attr = el.getAttribute('data-jl-stock-cities');
      if (attr) attr.split(',').forEach(c => {
        const t = c.trim().toLowerCase();
        if (t) foundCities.add(t);
      });
    });

    const handle = getCollectionHandle();
    const isChandelierFan = CHANDELIER_FAN_RE.test(handle);
    const isDecorative = DECORATIVE_RE.test(handle);

    const result = ['delhi']; // Delhi always shown and always first

    // Mumbai: always shown — it's the primary non-Delhi stock city
    result.push('mumbai');

    // Ahmedabad: show only if products with Ahmedabad stock are actually on this page
    if (foundCities.has('ahmedabad')) result.push('ahmedabad');

    // Bangalore: only for chandelier/fan collections
    if (isChandelierFan && foundCities.has('bangalore')) result.push('bangalore');

    // Chennai: only for non-decorative collections (JS-SMS family)
    if (!isDecorative && foundCities.has('chennai')) result.push('chennai');

    // Jaipur: never shown (serviced from Delhi)

    return result;
  }

  // ---------- PRODUCT SCORING ----------

  function extractStockCitiesFromCard(card) {
    const target = card.hasAttribute('data-jl-stock-cities')
      ? card
      : card.querySelector('[data-jl-stock-cities]');
    const attr = target ? target.getAttribute('data-jl-stock-cities') : null;
    if (!attr) return [];
    return attr.split(',').map(s => normalizeCity(s)).filter(Boolean);
  }

  function getBestLocationWeight(cities, customerCity) {
    if (cities.length === 0) return 0;
    let best = 0;
    for (const city of cities) {
      const w = getWeight(customerCity, city);
      if (w > best) best = w;
    }
    return best;
  }

  function combinedScore(card, originalIndex, totalCards, customerCity) {
    const baseScore = ((totalCards - originalIndex) / totalCards) * 100;
    const cities = extractStockCitiesFromCard(card);
    const locationWeight = getBestLocationWeight(cities, customerCity);
    const locationBoost = locationWeight * LOCATION_BOOST_POINTS;
    return baseScore + locationBoost;
  }

  function reorderProductCards(gridSelector, customerCity) {
    const grids = document.querySelectorAll(gridSelector);
    if (!grids.length) return 0;

    let reordered = 0;
    grids.forEach(grid => {
      const cards = Array.from(grid.children).slice(0, MAX_PRODUCTS_TO_RERANK);
      if (cards.length === 0) return;

      const anyHasData = cards.some(c => extractStockCitiesFromCard(c).length > 0);
      if (!anyHasData) return;

      const total = cards.length;
      const withScore = cards.map((card, idx) => ({
        card,
        score: combinedScore(card, idx, total, customerCity),
        originalIndex: idx,
      }));

      withScore.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.originalIndex - b.originalIndex;
      });

      withScore.forEach(item => grid.appendChild(item.card));
      reordered += cards.length;
    });

    return reordered;
  }

  // ---------- BANNER UI ----------

  function renderBanner(stockCity) {
    if (document.getElementById('jl-location-banner')) return;

    const displayName = getCityDisplayName(stockCity);
    const banner = document.createElement('div');
    banner.id = 'jl-location-banner';
    banner.className = 'jl-location-banner';
    banner.innerHTML = `
      <div class="jl-location-banner__inner">
        <span class="jl-location-banner__icon">📍</span>
        <span class="jl-location-banner__text">
          Showing products that ship from <strong>${escapeHtml(displayName)}</strong> first
        </span>
        <button type="button" class="jl-location-banner__change" data-jl-change-city>
          Change city
        </button>
        <button type="button" class="jl-location-banner__close" data-jl-close-banner aria-label="Close">
          ×
        </button>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      .jl-location-banner {
        background: #eef4ff;
        border: 1px solid #c2d7f5;
        border-radius: 6px;
        font-size: 0.95rem;
        padding: 0.6rem 1rem;
        color: #1a1a1a;
        margin-bottom: 0.75rem;
      }
      .jl-location-banner__inner {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      .jl-location-banner__icon { font-size: 1.2rem; }
      .jl-location-banner__text { flex: 1; font-size: 1rem; }
      .jl-location-banner__change {
        background: #1a56db;
        color: #fff;
        border: none;
        border-radius: 5px;
        padding: 0.4rem 1rem;
        cursor: pointer;
        font-size: 0.95rem;
        font-weight: 500;
        white-space: nowrap;
      }
      .jl-location-banner__change:hover { background: #1446b8; }
      .jl-location-banner__close {
        background: transparent;
        border: none;
        font-size: 1.4rem;
        padding: 0 0.3rem;
        cursor: pointer;
        color: #555;
        line-height: 1;
      }
      .jl-location-banner__close:hover { color: #111; }
    `;
    document.head.appendChild(style);

    const gridContainer = document.querySelector('#ProductGridContainer');
    if (gridContainer) {
      gridContainer.insertBefore(banner, gridContainer.firstChild);
    } else {
      const target = document.querySelector('main') || document.body;
      target.parentNode.insertBefore(banner, target);
    }

    banner.querySelector('[data-jl-close-banner]').addEventListener('click', () => {
      banner.remove();
    });

    banner.querySelector('[data-jl-change-city]').addEventListener('click', () => {
      openCityPicker(stockCity);
    });
  }

  function openCityPicker(currentCity) {
    const existingModal = document.getElementById('jl-city-picker');
    if (existingModal) existingModal.remove();

    const cities = discoverStockCitiesOnPage();
    const modal = document.createElement('div');
    modal.id = 'jl-city-picker';
    modal.innerHTML = `
      <div class="jl-city-picker__backdrop" data-jl-close-modal></div>
      <div class="jl-city-picker__content" role="dialog" aria-modal="true">
        <h3 style="margin-top:0">Choose your city</h3>
        <p style="color:#555; font-size:0.85rem; margin:0 0 0.75rem">
          We'll show products with stock nearest to you first.
        </p>
        <div class="jl-city-picker__options">
          ${cities.map(c => `
            <button type="button"
              class="jl-city-picker__option ${c === currentCity ? 'is-active' : ''}"
              data-jl-city="${c}">
              ${escapeHtml(getCityDisplayName(c))}
            </button>
          `).join('')}
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      #jl-city-picker {
        position: fixed; inset: 0; z-index: 9999;
        display: flex; align-items: center; justify-content: center;
      }
      .jl-city-picker__backdrop {
        position: absolute; inset: 0; background: rgba(0,0,0,0.4);
      }
      .jl-city-picker__content {
        position: relative;
        background: #fff; padding: 1.5rem; border-radius: 8px;
        min-width: 300px; max-width: 90vw;
        box-shadow: 0 10px 30px rgba(0,0,0,0.2);
      }
      .jl-city-picker__options { display: flex; flex-wrap: wrap; gap: 0.5rem; }
      .jl-city-picker__option {
        padding: 0.5rem 1rem;
        border: 1px solid #c9ced6;
        background: #fff; border-radius: 4px;
        cursor: pointer; font-size: 0.9rem;
      }
      .jl-city-picker__option.is-active {
        background: #1a1a1a; color: #fff; border-color: #1a1a1a;
      }
      .jl-city-picker__option:hover:not(.is-active) { background: #f4f6f9; }
    `;
    document.head.appendChild(style);
    document.body.appendChild(modal);

    modal.querySelector('[data-jl-close-modal]').addEventListener('click', () => {
      modal.remove();
    });
    modal.querySelectorAll('[data-jl-city]').forEach(btn => {
      btn.addEventListener('click', () => {
        const chosen = btn.getAttribute('data-jl-city');
        saveCity(chosen, 'user-selected');
        modal.remove();
        applySorter(chosen);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // ---------- MAIN ORCHESTRATOR ----------

  function isCollectionPage() {
    if (document.body.classList.contains('template-product')) return false;
    if (window.location.pathname.indexOf('/products/') !== -1) return false;
    return document.body.classList.contains('template-collection') ||
           document.querySelector('[data-jl-collection-grid]') !== null ||
           window.location.pathname.indexOf('/collections/') === 0;
  }

  function applySorter(stockCity) {
    const gridSelector = '[data-jl-collection-grid], #product-grid, .collection .grid--collection';
    const count = reorderProductCards(gridSelector, stockCity);
    const old = document.getElementById('jl-location-banner');
    if (old) old.remove();
    renderBanner(stockCity);
    if (window.console && count > 0) {
      console.log(`[jl-location-sorter] Sorted ${count} products for ${getCityDisplayName(stockCity)}`);
    }
  }

  function init() {
    if (!isCollectionPage()) return;

    // Use saved user preference if still valid
    const cached = loadCachedCity();
    if (cached) {
      applySorter(cached.stockCity);
      return;
    }

    // Default to Delhi — no auto IP detection; user can change via "Change city"
    saveCity('delhi', 'default');
    applySorter('delhi');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Console API for manual testing
  window.JLLocationSorter = {
    setCity: (city) => {
      const c = normalizeCity(city);
      if (!KNOWN_STOCK_CITIES.includes(c)) {
        console.warn(`[jl-location-sorter] Unknown city: ${city}. Valid: ${KNOWN_STOCK_CITIES.join(', ')}`);
        return;
      }
      saveCity(c, 'manual');
      applySorter(c);
    },
    getCurrentCity: () => {
      const cached = loadCachedCity();
      return cached ? getCityDisplayName(cached.stockCity) : 'Delhi';
    },
    clearCity: () => {
      try { localStorage.removeItem(LOCALSTORAGE_KEY); } catch (_e) {}
    },
    discoverCities: () => discoverStockCitiesOnPage().map(getCityDisplayName),
  };
})();
